
import { ref, defineComponent, computed, nextTick } from 'vue';
import { MultiFormBlockModel, MultiFormModel, CheckboxItemModel } from "@/models/Form";
import { FormApi } from "@/services/Api";
import { useI18n } from "vue-i18n";

export default defineComponent({
  props: {
    formdata: {
      type: Object as () => MultiFormBlockModel,
      required: true
    },
    culture: {
      type: String,
      required: true
    },
    emailto: {
      type: String,
      required: true
    },
    emailsubject: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const formData = ref<MultiFormBlockModel>(props.formdata);
    const isSubmitted = ref(false);
    const currentStep = ref(1);
    const totalSteps = 3;
    const displayResponseMsg = ref<boolean>(false);
    const responseMsg = ref<string>("");
    
    const stepOneValidationInfoText = t("MultiForm.StepOneValidationInfoText");
    const stepTwoValidationInfoText = t("MultiForm.StepTwoValidationInfoText");
    const stepThreeValidationInfoText = t("MultiForm.StepThreeValidationInfoText");

    const isStepOneValid = computed(() => {
      return formData.value.questionListStepOne.every((question) => question.selectedAnswer);
    });

    const isStepTwoValid = computed(() => {
      const { firstName, lastName, phoneNumber, email, address, postCode, city } = multiFormModel.value;
      return firstName && lastName && phoneNumber && email && address && postCode && city;
    });

    const isStepThreeValid = computed(() => {
      return formData.value.checkboxList.every((checkbox) => checkbox.selected);
    });

    const multiFormModel = ref<MultiFormModel>({
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      address: '',
      postCode: '',
      city: '',
      message: '',
      culture: props.culture,
      emailTo: props.emailto,
      emailSubject: props.emailsubject,
      answersStepOne: [],
      answersStepThree: []
    });

    const nextStep = async () => {
      if (currentStep.value < totalSteps) {
        currentStep.value++;
        focusOnCorrectElement();
        scrollToTopOfForm();
      } 
    };

    const prevStep = async () => {
      if (currentStep.value > 1) {
        currentStep.value--;
        await nextTick();
        const nextButtonElement = document.getElementById("nextStepButton");
        nextButtonElement?.focus();
        scrollToTopOfForm();
      } 
    };
    
    const focusOnCorrectElement = async () => {
      await nextTick();
      if (currentStep.value == 2) {
        const firstNameInputElement = document.getElementById("firstName");
        firstNameInputElement?.focus();
      } else if (currentStep.value == 3) {
        const firstInput = document.querySelector('input');
          if (firstInput) {
              firstInput.classList.add('no-focus-visible');
              firstInput.focus();
              setTimeout(() => firstInput.classList.remove('no-focus-visible'), 0);
          }
      }
    }

    const getCheckboxText = (checkbox: CheckboxItemModel) => {
      const linkHtml = `<a class="checkbox-text font-link" href="${checkbox.link.url}" target="${checkbox.link.target}">${checkbox.link.name}</a>`;
      return checkbox.text.replace("{link}", linkHtml);
    };

    const getSelectedOptions = (questionList) => {
      return questionList.map((question) => ({
        question: question.questionText,
        answer: question.selectedAnswer ? question.selectedAnswer : ""
      }));
    }

    const scrollToTopOfForm = () => {
      const element = document.getElementById("multiFormBlock");
      if (element) {
        const headerHeight = 81;
        const elementPosition = element?.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
      }
    }

    const toggleCheckbox = (index) => {
      formData.value.checkboxList[index].selected = !formData.value.checkboxList[index].selected;
    }

    const submitFormData = async () => {
      multiFormModel.value.answersStepOne = getSelectedOptions(formData.value.questionListStepOne);
      multiFormModel.value.answersStepThree = getSelectedOptions(formData.value.questionListStepThree);

      const response = await FormApi.sendMultiForm(multiFormModel.value);
      scrollToTopOfForm();
      if (response) {
        responseMsg.value = t("ContactForm.Success");
        isSubmitted.value = true;
      } else {
        responseMsg.value = t("ContactForm.Failure");
      }
    };

    return {
      formData,
      multiFormModel,
      currentStep,
      totalSteps,
      nextStep,
      prevStep,
      getCheckboxText,
      submitFormData,
      isSubmitted,
      isStepOneValid,
      isStepTwoValid,
      isStepThreeValid,
      stepOneValidationInfoText,
      stepTwoValidationInfoText,
      stepThreeValidationInfoText,
      displayResponseMsg,
      responseMsg,
      toggleCheckbox
    };
  }
});
